import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReservationRow } from '@smile/shared';
import { ApiError, api } from '../api/client';
import { CZK } from '../lib/format';
import { can } from '../lib/perms';

/**
 * Platba na místě — cash or card, on the booking page (the manager, 30 October 2026: "in the
 * booking's Platba there is no way to choose cash or card, only Fakturace or GoPay").
 *
 * The same write as the dashboard's Zaplaceno button (POST /payment → recordPayment): the amount
 * goes on the booking's on-site total with its method, journaled, and the shift's till counts it.
 * It does not close the booking — Odjezd does that. Shown while something is owed.
 */
export function DeskPayForm({ r }: { r: ReservationRow }) {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 10 * 60_000 });
  const owed = Math.max(0, Math.round(r.outstanding));
  const [amount, setAmount] = useState(String(owed));
  const [method, setMethod] = useState<'cash' | 'card'>('cash');
  const m = useMutation({
    mutationFn: () => api.pay(r.id, { amount: Math.round(Number(amount)), method }),
    onSuccess: async () => {
      await qc.invalidateQueries();
    },
  });
  const open = ['new', 'pending', 'paid', 'verified', 'closed'].includes(r.status);
  // The same grant the server checks on POST /payment (write on /reservations/*): a Řidič does not see it.
  if (!can(me.data, '/reservations/*', 'write') || !open || (owed <= 0 && !m.isSuccess)) return null;
  const n = Number(amount);
  const ok = Number.isInteger(n) && n > 0 && n <= 65_535;
  const err = m.error instanceof ApiError ? m.error.serverMessage ?? m.error.message : m.error instanceof Error ? m.error.message : null;
  return (
    <form
      className="arrive-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (ok && !m.isPending) m.mutate();
      }}
    >
      <span className="depart-owed">
        Platba na místě — zbývá: <strong>{CZK.format(owed)} Kč</strong>
      </span>
      <label>
        Částka
        <input type="number" inputMode="numeric" min={1} step={1} value={amount} onChange={(e) => setAmount(e.target.value)} />
      </label>
      <label>
        Způsob
        <select value={method} onChange={(e) => setMethod(e.target.value as 'cash' | 'card')}>
          <option value="cash">Hotově</option>
          <option value="card">Kartou</option>
        </select>
      </label>
      <button type="submit" className="btn sm primary" disabled={!ok || m.isPending}>
        {m.isPending ? 'Ukládám…' : 'Zaplaceno'}
      </button>
      {m.isSuccess && <span className="arrived-mark">✔ Platba zapsána</span>}
      {err && <span className="arrive-error">{err}</span>}
    </form>
  );
}
